/**
 * User-editable vendor color overrides, persisted in localStorage.
 *
 * The built-in brand colors live in `vendorCoverage.js` (VENDOR_METADATA).
 * This store lets users override those per-vendor from the picker UI. The
 * store is keyed by vendor id, so renames on the backend don't orphan
 * overrides. Subscribers are notified on every change (in-tab via the
 * in-memory listener set, cross-tab via the `storage` event).
 *
 * Shape in localStorage: { "<vendorId>": "#rrggbb", ... }
 */

const STORAGE_KEY = 'zt-vendor-color-overrides';

function readFromStorage() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeToStorage(overrides) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // Quota exceeded or storage disabled — ignore; state still lives in memory.
  }
}

let current = readFromStorage();
const listeners = new Set();

function emit() {
  for (const fn of listeners) fn();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    current = readFromStorage();
    emit();
  });
}

export function subscribeVendorColorOverrides(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getVendorColorOverrides() {
  return current;
}

export function getVendorColorOverride(vendorId) {
  if (vendorId == null) return null;
  return current[String(vendorId)] || null;
}

export function setVendorColorOverride(vendorId, hex) {
  if (vendorId == null) return;
  const normalized = normalizeHex(hex);
  if (!normalized) return;
  current = { ...current, [String(vendorId)]: normalized };
  writeToStorage(current);
  emit();
}

export function resetVendorColorOverride(vendorId) {
  if (vendorId == null) return;
  const key = String(vendorId);
  if (!(key in current)) return;
  const next = { ...current };
  delete next[key];
  current = next;
  writeToStorage(current);
  emit();
}

export function resetAllVendorColorOverrides() {
  if (Object.keys(current).length === 0) return;
  current = {};
  writeToStorage(current);
  emit();
}

/**
 * Resolve the effective color for a vendor: user override wins, then the
 * built-in `displayColor`, then a safe fallback.
 */
export function resolveVendorColor(vendor, fallback = '#3b82f6') {
  if (!vendor) return fallback;
  const override = getVendorColorOverride(vendor.id);
  if (override) return override;
  return vendor.displayColor || fallback;
}

function normalizeHex(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return null;
}
